import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaImage,
  FaExternalLinkAlt,
  FaFolder,
  FaServer,
  FaLink,
  FaPalette,
  FaExclamationTriangle,
  FaCamera
} from 'react-icons/fa';

const HTMLLesson12 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Basic Image Syntax -->
<img src="welcome.jpg" alt="Welcome banner" />

<!-- With dimensions -->
<img src="profile.png" alt="User profile picture" width="200" height="200" />

<!-- With CSS class -->
<img src="landscape.jpg" alt="Beautiful landscape" class="responsive-image" />`;

  const code2 = `<!-- Image with proper alt text -->
<img src="cat.jpg" alt="Fluffy orange cat sleeping on a sofa" />

<!-- Decorative image (empty alt) -->
<img src="divider.png" alt="" />

<!-- Complex image description -->
<img 
  src="chart.png" 
  alt="Bar chart showing quarterly sales growth: 
       Q1: 20%, Q2: 35%, Q3: 50%, Q4: 65%" 
/>`;

  const code3 = `<!-- Using width and height attributes -->
<img src="product.jpg" alt="Product image" width="400" height="300" />

<!-- Using CSS style attribute -->
<img 
  src="product.jpg" 
  alt="Product image" 
  style="width: 400px; height: 300px;" 
/>

<!-- Responsive image with CSS -->
<img 
  src="banner.jpg" 
  alt="Website banner" 
  class="responsive-img" 
/>

<style>
.responsive-img {
  max-width: 100%;
  height: auto;
}
</style>`;

  const code4 = `<!-- Image in same directory -->
<img src="logo.png" alt="Company logo" />

<!-- Image in subfolder -->
<img src="images/photo.jpg" alt="Team photo" />

<!-- Image in parent directory -->
<img src="../assets/icon.png" alt="App icon" />

<!-- Image from different folder structure -->
<img src="/static/images/hero.jpg" alt="Hero image" />`;

  const code5 = `<!-- External image from CDN -->
<img 
  src="https://images.unsplash.com/photo-1682687220742-aba13b6e50ba" 
  alt="Beautiful mountain landscape at sunset" 
  width="600" 
  height="400"
/>

<!-- Image from different domain -->
<img 
  src="https://example.com/images/logo.png" 
  alt="Partner company logo" 
/>

<!-- Secure external image -->
<img 
  src="https://secure.example.com/user-avatar.jpg" 
  alt="User avatar" 
  crossorigin="anonymous"
/>`;

  const code6 = `<!-- Image as link -->
<a href="/gallery">
  <img 
    src="thumbnail.jpg" 
    alt="View full gallery" 
    width="300" 
    height="200"
  />
</a>

<!-- Image with multiple links (image map) -->
<img 
  src="world-map.jpg" 
  alt="World map with clickable regions" 
  usemap="#worldmap" 
  width="800" 
  height="400"
/>

<map name="worldmap">
  <area shape="rect" coords="100,50,200,150" href="/europe" alt="Europe">
  <area shape="circle" coords="400,200,50" href="/asia" alt="Asia">
</map>`;

  const code7 = `<!-- Background image on body -->
<body style="
  background-image: url('clouds.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
">
  <h1>Welcome to Our Website</h1>
  <p>Content with beautiful background...</p>
</body>

<!-- Background image on specific element -->
<div class="hero-section">
  <h2>Amazing Hero Section</h2>
  <p>With background image</p>
</div>

<style>
.hero-section {
  background-image: linear-gradient(
      rgba(0, 0, 0, 0.5), 
      rgba(0, 0, 0, 0.5)
    ), url('hero-bg.jpg');
  background-size: cover;
  background-position: center;
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}
</style>`;

  const code8 = `<!DOCTYPE html>
<html>
<head>
  <style>
    .gallery {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
      padding: 2rem;
    }
    
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease;
    }
    
    .card:hover {
      transform: translateY(-4px);
    }
    
    .card img {
      width: 100%;
      height: 200px;
      object-fit: cover;
    }
    
    .card-content {
      padding: 1rem;
    }
    
    .hero {
      background-image: url('hero-background.jpg');
      background-size: cover;
      background-position: center;
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      text-align: center;
    }
    
    .avatar {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      border: 3px solid white;
    }
  </style>
</head>
<body>
  <!-- Hero section with background image -->
  <section class="hero">
    <div>
      <img src="logo-white.png" alt="Company Logo" class="avatar">
      <h1>Welcome to Our Gallery</h1>
      <p>Discover amazing images and photos</p>
    </div>
  </section>

  <!-- Image gallery -->
  <section class="gallery">
    <div class="card">
      <a href="/nature">
        <img 
          src="nature.jpg" 
          alt="Beautiful forest with sunlight filtering through trees"
        >
      </a>
      <div class="card-content">
        <h3>Nature Photography</h3>
        <p>Explore the beauty of natural landscapes</p>
      </div>
    </div>
    
    <div class="card">
      <a href="/urban">
        <img 
          src="city.jpg" 
          alt="Modern city skyline at dusk with colorful buildings"
        >
      </a>
      <div class="card-content">
        <h3>Urban Scenes</h3>
        <p>City life and architecture</p>
      </div>
    </div>
    
    <div class="card">
      <a href="/portraits">
        <img 
          src="portrait.jpg" 
          alt="Professional portrait of a smiling woman in studio lighting"
        >
      </a>
      <div class="card-content">
        <h3>Portraits</h3>
        <p>Professional portrait photography</p>
      </div>
    </div>
  </section>

  <!-- External image example -->
  <section style="text-align: center; padding: 2rem;">
    <h2>Featured from Unsplash</h2>
    <img 
      src="https://images.unsplash.com/photo-1541963463532-d68292c34b19" 
      alt="Open book on a wooden table with coffee" 
      width="400" 
      height="300"
      style="border-radius: 8px;"
    >
  </section>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Images Introduction",
      content: "Images enhance web design and user experience, making content more engaging and visually appealing",
      keyPoints: [
        "Use <img> tag for embedding images",
        "src attribute specifies image source",
        "alt attribute provides alternative text",
        "Self-closing tag with attributes only"
      ],
      icon: <FaImage className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "The Essential Alt Attribute", 
      content: "Alt text ensures accessibility and provides context when images cannot be displayed",
      keyPoints: [
        "Required for accessibility (screen readers)",
        "Displayed when image fails to load",
        "Should describe image content and function",
        "Empty alt for decorative images: alt=''"
      ],
      icon: <FaExclamationTriangle className="section-icon" />,
      color: "#dc2626",
      code: code2
    },
    {
      title: "Image Dimensions and Sizing",
      content: "Control image display size using attributes or CSS for optimal layout and performance",
      keyPoints: [
        "width/height attributes (pixels)",
        "CSS style attribute for precise control",
        "Responsive images with max-width: 100%",
        "Maintain aspect ratio with height: auto"
      ],
      icon: <FaCamera className="section-icon" />,
      color: "#059669",
      code: code3
    },
    {
      title: "Image Paths and Sources",
      content: "Understand different ways to reference images from local folders or external servers",
      keyPoints: [
        "Relative paths for local images",
        "Absolute paths for specific locations",
        "Subfolders for organized assets",
        "Parent directories with ../ notation"
      ],
      icon: <FaFolder className="section-icon" />,
      color: "#7c3aed",
      code: code4
    },
    {
      title: "External and CDN Images",
      content: "Leverage external image sources and content delivery networks for optimized loading",
      keyPoints: [
        "Full URLs for external images",
        "CDN benefits: faster delivery, caching",
        "Cross-origin attributes for security",
        "Consider bandwidth and copyright issues"
      ],
      icon: <FaServer className="section-icon" />,
      color: "#d97706",
      code: code5
    },
    {
      title: "Advanced Image Techniques",
      content: "Combine images with links and create interactive image maps for enhanced user experience",
      keyPoints: [
        "Wrap img in <a> tag for image links",
        "Image maps for clickable regions",
        "Remove borders with style='border: none'",
        "Combine with other HTML elements"
      ],
      icon: <FaLink className="section-icon" />,
      color: "#db2777",
      code: code6
    },
    {
      title: "Background Images with CSS",
      content: "Use CSS background-image property to create visually appealing backgrounds for elements",
      keyPoints: [
        "background-image: url('path')",
        "Control repetition with background-repeat",
        "Size options: cover, contain, specific values",
        "Position and attachment properties"
      ],
      icon: <FaPalette className="section-icon" />,
      color: "#0891b2",
      code: code7
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 12 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Images Mastery</h1>
              <p className="html-lesson-subtitle">
                Learn to effectively use images in HTML for engaging, accessible, and performant web design
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Image Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Image Implementation Power
              </div>
              <h3 className="html-visual-title">
                From Basic Images to Advanced Backgrounds - Complete Visual Control
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Basic Image Syntax</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<!-- Simple image -->
<img src="photo.jpg" alt="Description">

<!-- With dimensions -->
<img src="photo.jpg" alt="Description" 
     width="400" height="300">

<!-- Responsive image -->
<img src="photo.jpg" alt="Description" 
     class="responsive">`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Image Examples</h4>
                <div className="html-image-preview">
                  <div className="image-demo-item">
                    <div className="image-placeholder basic"></div>
                    <span>Basic Image</span>
                  </div>
                  <div className="image-demo-item">
                    <div className="image-placeholder sized"></div>
                    <span>With Dimensions</span>
                  </div>
                  <div className="image-demo-item">
                    <div className="image-placeholder linked"></div>
                    <span>Image Link</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Always include descriptive alt text for accessibility and SEO benefits!</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Image Attributes Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaExternalLinkAlt className="html-badge-icon" />
                Image Attributes Guide
              </div>
              <h3 className="html-comparison-title">
                Essential Image Attributes and Their Uses
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Attribute</th>
                    <th>Purpose</th>
                    <th>Required</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>src</code></td>
                    <td>Specifies image source path/URL</td>
                    <td>✅ Yes</td>
                    <td><code>src="image.jpg"</code></td>
                  </tr>
                  <tr>
                    <td><code>alt</code></td>
                    <td>Alternative text for accessibility</td>
                    <td>✅ Yes</td>
                    <td><code>alt="Company logo"</code></td>
                  </tr>
                  <tr>
                    <td><code>width</code></td>
                    <td>Sets image width in pixels</td>
                    <td>❌ Optional</td>
                    <td><code>width="300"</code></td>
                  </tr>
                  <tr>
                    <td><code>height</code></td>
                    <td>Sets image height in pixels</td>
                    <td>❌ Optional</td>
                    <td><code>height="200"</code></td>
                  </tr>
                  <tr>
                    <td><code>loading</code></td>
                    <td>Controls lazy loading behavior</td>
                    <td>❌ Optional</td>
                    <td><code>loading="lazy"</code></td>
                  </tr>
                  <tr>
                    <td><code>title</code></td>
                    <td>Tooltip text on hover</td>
                    <td>❌ Optional</td>
                    <td><code>title="Click to enlarge"</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Image Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional Image Implementation Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use descriptive, concise alt text</li>
                  <li>Specify width and height attributes</li>
                  <li>Optimize images for web (compression)</li>
                  <li>Use appropriate file formats (JPEG, PNG, WebP)</li>
                  <li>Implement lazy loading for performance</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using images for text content</li>
                  <li>Overly large file sizes</li>
                  <li>Missing alt attributes</li>
                  <li>Stretching images disproportionately</li>
                  <li>Using copyrighted images without permission</li>
                </ul>
              </div>
            </div>
          </div>

          {/* File Formats Guide */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaImage className="html-badge-icon" />
                Image File Formats
              </div>
              <h3 className="html-multiple-title">
                Choosing the Right Format for Your Images
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>Common Image Formats:</h4>
                <ul>
                  <li><strong>JPEG (.jpg, .jpeg):</strong> Best for photographs, supports millions of colors, lossy compression</li>
                  <li><strong>PNG (.png):</strong> Lossless compression, supports transparency, good for graphics and logos</li>
                  <li><strong>GIF (.gif):</strong> Supports animation, limited to 256 colors, good for simple graphics</li>
                  <li><strong>WebP (.webp):</strong> Modern format, better compression than JPEG/PNG, supports transparency and animation</li>
                  <li><strong>SVG (.svg):</strong> Vector format, scalable without quality loss, perfect for icons and logos</li>
                </ul>
              </div>
              
              <div className="html-format-examples">
                <h4>Format Selection Guide:</h4>
                <div className="format-grid">
                  <div className="format-item">
                    <div className="format-icon jpeg"></div>
                    <span>Photos → JPEG</span>
                  </div>
                  <div className="format-item">
                    <div className="format-icon png"></div>
                    <span>Logos → PNG</span>
                  </div>
                  <div className="format-item">
                    <div className="format-icon svg"></div>
                    <span>Icons → SVG</span>
                  </div>
                  <div className="format-item">
                    <div className="format-icon gif"></div>
                    <span>Animations → GIF</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Image Gallery with Multiple Techniques
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code8}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Image Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML images?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive image implementation including basic syntax, accessibility, responsive design, external sources, and advanced techniques. Start creating visually engaging websites!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-colors" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson12;